import {
  type LessonItem,
  addDays,
  clubsOnDay,
  timeOf,
  useClubMemberships,
  useClubs,
} from '@shkola/api';
import { tones } from '@shkola/design';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowUpRightIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CheckCircleIcon,
  SunHorizonIcon,
} from '../../design/icons';
import { buttonClass, useAge } from '../../design/ui';
import { useMinute } from '../../lib/now';
import { BreakRow, EdgeRow, LessonCard, LessonRow, PackPanel, Rail, useLessonGrades } from './Day';
import { ClubRow, EVENT_LOOK, EventRow, Gap, type Week, useWeek } from './GlassDay';
import {
  type Slot,
  type StudentContext,
  capitalize,
  dayLong,
  dayNow,
  isoWeekday,
  minutesBetween,
  monthName,
  slotsOf,
  weekdayShort,
} from './model';

/*
 * The lessons of «Свіжість»: the week on top and the day of «Скло» (his words 26–27.10: the days to
 * click, the breaks without big gaps), drawn the way of «Свіжість» — the days on its lime-into-lilac band
 * (ref. «October 2023»: the days tinted by the band, the chosen one white), the timeline beside the rows
 * that the time gone by fills, the lessons in their subjects' colours; a break is a narrow line with its
 * length, the one going on in colour. After the lessons — the student's club; for the next day — what to
 * pack. A click on a lesson opens its card.
 */

/** The ground of «Свіжість»: lime and mint into blue and lilac (ref. «October 2023»). */
export const FRESH_GROUND = [
  'radial-gradient(40% 70% at 100% 0%, rgb(255 255 255 / 0.45) 0%, rgb(255 255 255 / 0) 70%)',
  'linear-gradient(115deg, #E6F5A2 0%, #D2EAD2 36%, #C9DCEB 70%, #DAD4F4 100%)',
].join(', ');

/** The hairline of a white block of «Свіжість». */
export const FRESH_RING = 'ring-1 ring-[#E1EAE4] ring-inset';

/** The days on the band: the weekday and the date, a red dot for a test and a dot of an event. */
function Days({ week }: { week: Week }) {
  const { t } = useTranslation();
  return (
    <div
      className="rim grid gap-1.5 rounded-[20px] p-1.5"
      style={{
        gridTemplateColumns: `repeat(${week.days.length}, minmax(0, 1fr))`,
        backgroundImage: FRESH_GROUND,
      }}
    >
      {week.days.map(({ date, lessons, events }) => {
        const on = date === week.selected;
        const isToday = date === week.today;
        const test = lessons.some((l) => l.isTest && l.status !== 'cancelled');
        const event = events.find((e) => e.kind !== 'holiday');
        const off = lessons.length === 0;
        return (
          <button
            key={date}
            type="button"
            aria-pressed={on}
            onClick={() => week.select(date)}
            title={isToday ? t('studentHome.day.today') : undefined}
            className={`relative flex h-[62px] min-w-0 flex-col items-center justify-center rounded-[15px] transition-[background-color,box-shadow] sm:flex-row sm:items-baseline sm:gap-1.5 ${
              on
                ? 'bg-white shadow-[0_6px_16px_-8px_rgb(30_42_94/0.35)]'
                : 'bg-[rgb(30_42_94/0.06)] hover:bg-white/60'
            }`}
          >
            <span
              className={`text-[12.5px] leading-4 font-medium ${on ? 'text-muted' : off ? 'text-faint' : 'text-ink-soft'}`}
            >
              {weekdayShort(date)}
            </span>
            <span
              className={`font-display text-[24px] leading-7 font-semibold tracking-[-0.02em] ${off && !on ? 'text-faint' : 'text-ink'}`}
              style={on ? { color: tones.violet.vivid } : undefined}
            >
              {Number(date.slice(8))}
            </span>
            <span
              aria-hidden
              className="absolute bottom-1.5 left-1/2 flex h-1.5 -translate-x-1/2 items-center gap-[3px]"
            >
              {test && (
                <span
                  className="size-1.5 rounded-full"
                  style={{ backgroundColor: tones.red.vivid }}
                />
              )}
              {event && (
                <span
                  className="size-1.5 rounded-full"
                  style={{ backgroundColor: tones[EVENT_LOOK[event.kind].tone].vivid }}
                />
              )}
            </span>
            {isToday && (
              <span
                aria-hidden
                className="absolute top-2 right-2 size-1.5 rounded-full bg-tone-blue-vivid"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

const roundButton = `inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-white text-ink ring-1 ring-stroke ring-inset transition-colors hover:bg-soft`;

export function FreshDay({ ctx, className = '' }: { ctx: StudentContext; className?: string }) {
  const { t } = useTranslation();
  const now = useMinute();
  const age = useAge();
  const week = useWeek(ctx);
  const clubs = useClubs(ctx.schoolId);
  const memberships = useClubMemberships([ctx.child.memberId]);
  const [open, setOpen] = useState<{ lesson: LessonItem; el: HTMLElement }>();
  const [unfolded, setUnfolded] = useState(false);
  const gradesOf = useLessonGrades(ctx, week.selected);
  const isToday = week.selected === week.today;
  const status = dayNow(week.lessons, now);
  const held = status.held;
  const slots = slotsOf(week.lessons);
  const holiday = week.events.find((e) => e.kind === 'holiday');
  const dayEvents = week.events.filter((e) => e.kind !== 'holiday');
  const dayClubs =
    held.length > 0
      ? clubsOnDay(
          clubs.data ?? [],
          memberships.data ?? [],
          ctx.child.memberId,
          isoWeekday(week.selected),
        )
      : [];
  const nextDay = week.anchor !== week.today && week.selected === week.anchor;

  type Row =
    | { key: string; kind: 'before' }
    | { key: string; kind: 'over' }
    | { key: string; kind: 'slot'; slot: Slot; index: number };
  const rows: Row[] = [
    ...(isToday && status.before ? [{ key: 'before', kind: 'before' as const }] : []),
    ...slots.map((slot, index) => ({
      key: slot.kind === 'lesson' ? slot.lesson.id : `break-${slot.start}`,
      kind: 'slot' as const,
      slot,
      index,
    })),
    ...(isToday && status.over ? [{ key: 'over', kind: 'over' as const }] : []),
  ];

  // on a phone: from what goes on now three lessons on, the rest folded
  const focus = isToday
    ? Math.max(
        0,
        slots.findIndex(
          (s) =>
            (s.start <= now && now < s.end) || (s.kind === 'lesson' && s.lesson === status.next),
        ),
      )
    : 0;
  const lessonIndexes = slots
    .map((s, i) => (s.kind === 'lesson' ? i : -1))
    .filter((i) => i >= 0 && i >= focus);
  const lastShown = lessonIndexes[2] ?? slots.length - 1;
  const folded = (i: number) => !unfolded && (i < focus || i > lastShown);
  const foldedCount = slots.filter((s, i) => s.kind === 'lesson' && folded(i)).length;
  const anchorLabel =
    week.anchor === week.today
      ? t('studentHome.day.today')
      : week.anchor === addDays(week.today, 1)
        ? t('studentHome.day.tomorrow')
        : capitalize(weekdayShort(week.anchor));

  return (
    <section
      className={`flex min-w-0 flex-col rounded-[28px] bg-white p-4 md:p-6 ${FRESH_RING} [&_.break-row]:my-0.5 [&_.break-row]:h-7 ${className}`}
    >
      <header className="mb-4 flex min-h-8 items-center gap-2">
        <h2
          className={`font-display font-semibold tracking-[-0.01em] text-ink ${age === 'junior' ? 'text-[22px] leading-7' : 'text-[18px] leading-6'}`}
        >
          {t('studentHome.glass.week.title')}
        </h2>
        <span className="truncate text-[14px] text-muted">{monthName(week.selected)}</span>
        <span className="ml-auto flex shrink-0 items-center gap-1.5">
          {week.selected !== week.anchor && (
            <button
              type="button"
              onClick={week.back}
              className="inline-flex h-8 items-center rounded-full bg-primary px-3 text-[12.5px] font-semibold text-white transition-colors hover:bg-primary-hover"
            >
              {anchorLabel}
            </button>
          )}
          <button
            type="button"
            aria-label={t('studentHome.glass.week.prev')}
            onClick={() => week.shift(-1)}
            className={roundButton}
          >
            <CaretLeftIcon size={13} weight="bold" aria-hidden />
          </button>
          <button
            type="button"
            aria-label={t('studentHome.glass.week.next')}
            onClick={() => week.shift(1)}
            className={roundButton}
          >
            <CaretRightIcon size={13} weight="bold" aria-hidden />
          </button>
          <Link
            to="/schedule"
            aria-label={t('studentHome.glass.week.all')}
            title={t('studentHome.glass.week.all')}
            className={`${roundButton} duration-500 ease-spring hover:rotate-45`}
          >
            <ArrowUpRightIcon size={14} weight="bold" aria-hidden />
          </Link>
        </span>
      </header>

      <Days week={week} />

      <p className="mt-3 mb-2 truncate text-[13px] text-muted">
        <span className="font-semibold text-ink">{dayLong(week.selected)}</span>
        {held[0] &&
          ` · ${t('studentHome.glass.week.summary', {
            lessons: t('student.lessonsCount', { count: held.length }),
            from: timeOf(held[0].startsAt),
            to: timeOf((held.at(-1) as LessonItem).endsAt),
          })}`}
      </p>

      {week.pending ? (
        <div className="flex flex-col gap-1.5 pl-7" aria-busy>
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} className="h-[56px] animate-pulse rounded-[14px] bg-soft" />
          ))}
        </div>
      ) : week.lessons.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 py-8 text-center">
          <span
            className="rim flex size-14 items-center justify-center rounded-full"
            style={{ backgroundImage: FRESH_GROUND, color: tones.green.vivid }}
          >
            <SunHorizonIcon size={26} weight="fill" aria-hidden />
          </span>
          <p className="text-[15px] font-semibold text-ink">
            {holiday?.title ?? t('studentHome.glass.week.weekend')}
          </p>
          <p className="text-[13px] text-muted">{t('studentHome.glass.week.none')}</p>
          {week.selected !== week.anchor && (
            <button
              type="button"
              onClick={week.back}
              className={`mt-2 ${buttonClass('outline', 'sm')}`}
            >
              {t('studentHome.glass.week.nextDay', { day: dayLong(week.anchor) })}
            </button>
          )}
        </div>
      ) : (
        <>
          {dayEvents.length > 0 && (
            <div className="mb-1.5 flex flex-col gap-1.5 pl-7">
              {dayEvents.map((e) => (
                <EventRow key={e.id} event={e} />
              ))}
            </div>
          )}
          <ol>
            {rows.map((row, i) => {
              const isFirst = i === 0;
              const isLast = i === rows.length - 1;
              if (row.kind === 'before' && status.first)
                return (
                  <li key={row.key} className="flex gap-3">
                    <Rail fill={0} first={isFirst} last={isLast} nowAt={0.5} />
                    <div className="min-w-0 flex-1">
                      <EdgeRow tone="teal" live>
                        {t('studentHome.day.beforeFull', {
                          min: minutesBetween(now, Date.parse(status.first.startsAt)),
                        })}
                      </EdgeRow>
                    </div>
                  </li>
                );
              if (row.kind === 'over' && status.last)
                return (
                  <li key={row.key} className="flex gap-3">
                    <Rail fill={1} first={isFirst} last={isLast} nowAt={0.5} />
                    <div className="min-w-0 flex-1">
                      <EdgeRow tone="green" icon={CheckCircleIcon}>
                        {t('studentHome.day.over', { time: timeOf(status.last.endsAt) })}
                      </EdgeRow>
                    </div>
                  </li>
                );
              if (row.kind !== 'slot') return null;
              const { slot } = row;
              const hidden = folded(row.index) ? 'max-md:hidden' : '';
              const gone = isToday ? (now - slot.start) / (slot.end - slot.start) : 0;
              const inside = isToday && slot.start <= now && now < slot.end;
              if (slot.kind === 'break')
                return (
                  <li key={row.key} className={`flex gap-3 ${hidden}`}>
                    <Rail
                      fill={gone}
                      first={isFirst}
                      last={isLast}
                      nowAt={inside ? gone : undefined}
                    />
                    <div className={`min-w-0 flex-1 ${inside ? '' : 'py-0.5'}`}>
                      {inside ? <BreakRow slot={slot} now={now} current /> : <Gap slot={slot} />}
                    </div>
                  </li>
                );
              const l = slot.lesson;
              const prev = slots[row.index - 1];
              const cancelled = l.status === 'cancelled';
              const state = cancelled
                ? 'ahead'
                : isToday && l === status.live
                  ? 'live'
                  : isToday && slot.end <= now
                    ? 'done'
                    : (isToday && l === status.next) || (!isToday && l === held[0] && nextDay)
                      ? 'next'
                      : 'ahead';
              return (
                <li key={row.key} className={`flex gap-3 ${hidden}`}>
                  <Rail
                    fill={gone}
                    first={isFirst}
                    last={isLast}
                    node={state === 'live' ? undefined : cancelled ? 'off' : state}
                    nowAt={state === 'live' ? gone : undefined}
                  />
                  {/* two lessons with no break between them: a hair of a gap */}
                  <div className={`min-w-0 flex-1 ${prev?.kind === 'lesson' ? 'pt-1' : ''}`}>
                    <LessonRow
                      lesson={l}
                      state={state}
                      now={now}
                      isToday={isToday}
                      grades={gradesOf(l)}
                      onOpen={(lesson, el) => setOpen({ lesson, el })}
                    />
                  </div>
                </li>
              );
            })}
          </ol>
          {foldedCount > 0 && (
            <button
              type="button"
              onClick={() => setUnfolded(true)}
              className="mt-2 ml-7 self-start rounded-full bg-soft px-3.5 py-1.5 text-[13px] font-semibold text-ink-soft hover:text-ink md:hidden"
            >
              {t('studentHome.day.more', { count: foldedCount })}
            </button>
          )}
          {dayClubs.length > 0 && (
            <div className="mt-2 flex flex-col gap-1.5 pl-7">
              {dayClubs.map(({ club, slot }) => (
                <ClubRow key={`${club.id}-${slot.startsAt}`} club={club} slot={slot} />
              ))}
            </div>
          )}
          {nextDay && <PackPanel ctx={ctx} date={week.selected} />}
        </>
      )}
      {open && (
        <LessonCard
          ctx={ctx}
          lesson={open.lesson}
          anchor={open.el}
          onClose={() => setOpen(undefined)}
        />
      )}
    </section>
  );
}
